/* eslint-disable no-console -- . */
import {
  useGetUserMetricsQuery,
  usePlaceAffiliateOrderMutation,
  useRecalculateAffiliateOrderLazyQuery,
  type PaymentGateway,
  type StepsType,
} from '@/affiliate-dashboard/graphql';
import { Button } from '@/components/base/button';
import { FormCheckbox } from '@/components/base/form/form-checkbox';
import Section from '@/components/base/section';
import { TermsCheckbox } from '@/components/base/terms-checkbox';
import { SelectAccountSize } from '@/components/order/select-account-size';
import { useAppConfig } from '@/context/app-config-context';
import { SelectAccountSteps } from '@/monevis-platform/components/order/steps/select-account-steps';
import { isBannedCountry } from '@/utils/country-list';
import { isEuropeanUnion } from '@/utils/order-utils';
import { type AddonType } from '@graphql/index';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type OrderAffiliateData } from '../../../../graphql';
import { AddDiscountCode } from './add-discount-code';
import { AffiliateCode } from './affiliate-code';
import { Info } from './info';
import { OrderFormWrapper, type Order } from './order-form-wrapper';
import { OrderSummary } from './order-summary';
import { PersonalInfoForm, PersonalInfoMode } from './personal-info-form';
import { SelectAddons } from './select-addons';

function OrderForm({
  promo,
  balance,
  affiliateCode,
}: {
  promo?: string;
  balance?: number;
  affiliateCode?: string | null;
}): JSX.Element {
  const [placeAffiliateOrder] = usePlaceAffiliateOrderMutation();
  const [recalculateAffiliateOrder] = useRecalculateAffiliateOrderLazyQuery();
  const [loading, setLoading] = useState(false);
  const [finalPrice, setFinalPrice] = useState(0);
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext<Order>();
  const watchCountry = watch('countryCode');
  const watchAddons = watch('addons');
  const [isUserFilled, setIsUserFilled] = useState<PersonalInfoMode>(
    PersonalInfoMode.EMPTY,
  );

  const { data: metrics } = useGetUserMetricsQuery();
  const [enteredAffiliateCode, setEnteredAffiliateCode] = useState<
    string | null | undefined
  >(affiliateCode);

  const {
    AppConfig: { REFUND_POLICY_URL },
  } = useAppConfig();
  const { t } = useTranslation();
  const accountSizeSectionRef = useRef<HTMLDivElement>(null);

  async function placeOrder(
    paymentMethod: PaymentGateway,
    orderData: Order,
    orderAffiliateData: OrderAffiliateData,
  ): Promise<void> {
    const {
      refundPolicy: _refundPolicy,
      termsPolicy: _termsPolicy,
      ...data
    } = orderData;

    const orderAddress = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      countryCode: data.countryCode,
    };

    const _orderAffiliateData: OrderAffiliateData = {};
    if (orderAffiliateData?.affiliateCode) {
      _orderAffiliateData.affiliateCode = orderAffiliateData.affiliateCode;
    }
    if (orderAffiliateData?.refCode) {
      _orderAffiliateData.refCode = orderAffiliateData.refCode;
    }

    try {
      setLoading(true);
      // const _summary = await recalculateAffiliateOrder({
      //   variables: {
      //     accountSize: Number(data.initialBalance),
      //     couponCode: data.discountCode,
      //     addonTypesOnOrder: data.addons,
      //     stepsType: data.accountType,
      //   },
      // });
      console.log('Placing order');
      const response = await placeAffiliateOrder({
        variables: {
          affiliateOrderInput: {
            ...orderAddress,
            accountSize: Number(data.initialBalance),
            addons: data.addons,
            //FIXME
            paymentRequired: true,
            paymentGateway: paymentMethod,
            couponCode: data.discountCode,
            stepsType: data.accountType,
          },
        },
      });
      window.location.href = String(response?.data?.affiliateOrder.paymentUrl);
    } catch (e) {
      if (e instanceof Error) {
        if (e.message !== 'Unauthorized') {
          enqueueSnackbar({
            message: e.message,
            variant: 'error',
          });
        }
        throw new Error(e.message);
      }
    } finally {
      setLoading(false);
    }
  }
  console.log('VALUES', getValues());

  async function handleRecalculateOrder(): Promise<void> {
    const accountSize = getValues('initialBalance');
    const couponCode = getValues('discountCode');
    const addonTypesOnOrder = getValues('addons');
    const accountType = getValues('accountType');

    const summary = await recalculateAffiliateOrder({
      variables: {
        accountSize,
        couponCode,
        addonTypesOnOrder,
        stepsType: accountType,
      },
    });
    setFinalPrice(summary.data?.recalculateAffiliateOrder ?? 0);
  }

  function handleAddonsChange(addon: AddonType): void {
    const currentAddons = getValues('addons') ?? [];
    if (currentAddons.includes(addon)) {
      setValue(
        'addons',
        currentAddons.filter((a) => a !== addon),
      );
    } else {
      setValue('addons', [...currentAddons, addon]);
    }
    handleRecalculateOrder();
  }

  function handleBalanceChange(value: number): void {
    setValue('initialBalance', value);
    handleRecalculateOrder();
  }

  function handleAccountTypeChange(value: StepsType): void {
    setValue('accountType', value);
    handleRecalculateOrder();
  }

  function handleDiscountCodeChange(value: string): void {
    setValue('discountCode', value);
    handleRecalculateOrder();
  }

  useEffect(() => {
    if (promo) {
      setValue('discountCode', promo);
    } else if (metrics?.userMetrics?.couponCodes?.length) {
      setValue(
        'discountCode',
        metrics?.userMetrics?.couponCodes[0]?.couponCodeRequested ?? '',
      );
    }
  }, [promo, metrics, setValue]);

  return (
    <>
      <Info />
      <PersonalInfoForm
        watchCountry={watchCountry}
        changeMode={(mode: PersonalInfoMode) => {
          setIsUserFilled(mode);
        }}
        geolocation={null}
        isUserFilled={isUserFilled}
      />

      {isUserFilled ? (
        <>
          <SelectAccountSteps
            //@ts-expect-error -- FIX
            onChange={(type) => {
              //@ts-expect-error -- FIX
              handleAccountTypeChange(type);
            }}
            //@ts-expect-error -- FIX
            initialState={getValues('accountType')}
          />
          <div ref={accountSizeSectionRef}>
            <SelectAccountSize
              currency={
                isEuropeanUnion(getValues('countryCode')) ? 'EUR' : 'USD'
              }
              hardBalance={balance}
              onChange={(b) => {
                handleBalanceChange(b);
              }}
              initialState={getValues('initialBalance')?.toString()}
              finalPrice={finalPrice}
            />
          </div>
          <SelectAddons
            onSelect={(addon) => {
              handleAddonsChange(addon);
            }}
            currentAddons={watchAddons}
            currentBalance={balance}
          />

          <AddDiscountCode
            handleAddDiscount={(v) => {
              handleDiscountCodeChange(v);
            }}
          />
          <AffiliateCode
            onChange={(e) => {
              setEnteredAffiliateCode(e);
            }}
          />
          <div className="space-y-4">
            <FormCheckbox data-testid="refund-checkbox" name="refundPolicy">
              <span>
                <span>{t('personalInfoForm.refundPolicyTextPart1')}</span>
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={REFUND_POLICY_URL}
                  className="underline underline-offset-2"
                >
                  {t('personalInfoForm.refundPolicyLinkText')}
                </a>
              </span>
            </FormCheckbox>

            <TermsCheckbox
              name="termsPolicy"
              description={t('personalInfoForm.termsPolicyDescription')}
            />
          </div>
        </>
      ) : null}
      {finalPrice > 0 && !isBannedCountry(watchCountry) ? (
        <OrderSummary
          total={finalPrice}
          subTotal={0}
          loading={loading}
          currency={isEuropeanUnion(getValues('countryCode')) ? 'EUR' : 'USD'}
          onSubmit={(method) => {
            console.log('Submitting order with errors', errors);
            handleSubmit(() => {
              placeOrder(method, getValues(), {
                refCode: enteredAffiliateCode,
              });
            })();
          }}
        />
      ) : (
        <div className="flex justify-center">
          <Button
            onClick={() => {
              accountSizeSectionRef.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }}
            className="w-full max-w-96"
            type="button"
          >
            {t('orderSummary.pay')}
          </Button>
        </div>
      )}
    </>
  );
}

export function Order({
  promo,
  balance,
  affiliateCode,
}: {
  promo?: string;
  balance?: number;
  affiliateCode?: string | null;
}): JSX.Element {
  return (
    <Section variant="secondary">
      <div className="mx-auto w-full space-y-8">
        <OrderFormWrapper>
          <OrderForm
            promo={promo}
            balance={balance}
            affiliateCode={affiliateCode}
          />
        </OrderFormWrapper>
      </div>
    </Section>
  );
}
